// OEM QUOTES -- the register of quotes our suppliers gave us
// (oem-quote-register, 2026-10-06, D119).
//
// Every role reads it; Sales and Admin create and edit. ⚠️ THE SCREEN IS NOT
// THE GUARD: /save_oem_quote and /preview_oem_quote_lines refuse a Viewer with
// a 403 whatever this renders, and the server owns every rule (models/
// oem_quote.py). What the server says is shown verbatim. Nothing is deleted.
//
// Lines are typed in, or uploaded from a CSV/.xlsx: the upload is PREVIEWED
// and SAVES NOTHING -- its good rows join the editor, and the user saves the
// whole OEM quote (the same pattern as Duty Rates).
//
// ⚠️ INTERNAL SUPPLIER DATA. An OEM quote is used as a customer line's net
// cost on the quote screen; its OEM, number and price never reach the
// customer document.

import { useCallback, useEffect, useState } from "react";
import {
  listOemQuotes, getOemQuote, saveOemQuote, previewOemQuoteLines, OEM_CURRENCIES,
  type OemQuote, type OemQuoteEdit, type OemQuoteLine, type OemQuotePreviewRow, type OemCurrency,
} from "@/services/api";
import { useCanEditCustomersOrFalse } from "@/auth/AuthContext";
import { formatDate } from "@/lib/format";
import { HowItWorks } from "@/components/DealProgress";
import { colors, semanticColors, radius, spacing, heights, typography } from "@/styles/tokens";

const INPUT: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[8]}px`,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.base, backgroundColor: semanticColors.surface,
  color: semanticColors.textPrimary, boxSizing: "border-box", width: "100%",
};
const BUTTON: React.CSSProperties = {
  height: heights.control, padding: `0 ${spacing[14]}px`,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.base, backgroundColor: semanticColors.surface,
  color: semanticColors.textPrimary, cursor: "pointer", whiteSpace: "nowrap",
};
const PRIMARY: React.CSSProperties = {
  ...BUTTON, border: "none", backgroundColor: colors.indigo600,
  color: semanticColors.onAccent, fontWeight: typography.fontWeight.medium,
};
const BOX: React.CSSProperties = {
  marginBottom: spacing[16], padding: spacing[12],
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  backgroundColor: semanticColors.panelBg,
  display: "flex", flexDirection: "column", gap: spacing[8],
};
const NOTE: React.CSSProperties = { fontSize: typography.fontSize.sm, color: semanticColors.textSecondary };
const ERROR: React.CSSProperties = { ...NOTE, color: semanticColors.destructive };
const LABEL: React.CSSProperties = { ...NOTE, display: "flex", flexDirection: "column", gap: spacing[4] };
const TH: React.CSSProperties = {
  textAlign: "left", padding: `${spacing[6]}px ${spacing[8]}px`,
  fontSize: typography.fontSize.xs, color: semanticColors.textMuted,
  fontWeight: typography.fontWeight.medium, borderBottom: `1px solid ${semanticColors.border}`,
  whiteSpace: "nowrap", backgroundColor: semanticColors.surface,
};
const TD: React.CSSProperties = {
  padding: `${spacing[6]}px ${spacing[8]}px`, fontSize: typography.fontSize.base,
  borderBottom: `1px solid ${semanticColors.border}`, whiteSpace: "nowrap", verticalAlign: "top",
};
const PILL: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", height: heights.pill, padding: `0 ${spacing[8]}px`,
  borderRadius: radius.base, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.medium,
  border: `1px solid ${semanticColors.warningBorder}`, backgroundColor: semanticColors.warningBg,
  color: colors.amber800,
};

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("The file could not be read."));
    reader.onload = () => {
      const text = String(reader.result ?? "");
      resolve(text.slice(text.indexOf(",") + 1));
    };
    reader.readAsDataURL(file);
  });
}

const EMPTY_LINE: OemQuoteLine = { part_number: "", qty: 1, unit_price: 0 };

function blank(): OemQuoteEdit {
  return {
    oem: "", number: "", quote_date: new Date().toISOString().slice(0, 10), valid_until: "",
    project_name: "", currency: "INR", notes: "", lines: [{ ...EMPTY_LINE }],
  };
}

function toEdit(q: OemQuote): OemQuoteEdit {
  return {
    id: q.id, oem: q.oem, number: q.number, quote_date: q.quote_date ?? "", valid_until: q.valid_until ?? "",
    project_name: q.project_name ?? "", currency: q.currency, notes: q.notes ?? "",
    lines: (q.lines ?? []).map((l) => ({ ...l })),
  };
}

/** The editor's state as the save payload: blanks as null, numbers as numbers. */
function toPayload(e: OemQuoteEdit): OemQuoteEdit {
  const s = (v: string | null | undefined) => (v && v.trim() ? v.trim() : null);
  return {
    ...e, oem: e.oem.trim(), number: e.number.trim(), quote_date: s(e.quote_date),
    valid_until: s(e.valid_until), project_name: s(e.project_name), notes: s(e.notes),
    lines: e.lines.map((l) => ({
      ...(l.id ? { id: l.id } : {}),
      part_number: l.part_number.trim(), description: s(l.description), qty: Number(l.qty),
      uom: s(l.uom), unit_price: Number(l.unit_price),
      extended_price: l.extended_price === null || l.extended_price === undefined || String(l.extended_price) === ""
        ? null : Number(l.extended_price),
      lead_time: s(l.lead_time), est_ship_date: s(l.est_ship_date), shipping_method: s(l.shipping_method),
    })),
  };
}

export default function OemQuotesPage() {
  const canEdit = useCanEditCustomersOrFalse();
  const [rows, setRows] = useState<OemQuote[] | null>(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<OemQuoteEdit | null>(null);
  // Remounts the editor on open / new only -- never on its own save.
  const [editorKey, setEditorKey] = useState(0);
  const startEditing = (e: OemQuoteEdit) => { setEditorKey((k) => k + 1); setEditing(e); };

  const load = useCallback(async (q: string) => {
    const r = await listOemQuotes(q);
    if (!r.ok) { setRows(null); setError(r.error ?? "The OEM quotes could not be read."); return; }
    setError("");
    setRows(r.oemQuotes ?? []);
  }, []);
  useEffect(() => {
    const t = setTimeout(() => { void load(query); }, 250);
    return () => clearTimeout(t);
  }, [query, load]);

  const open = async (id: number) => {
    const r = await getOemQuote(id);
    if (!r.ok || !r.oemQuote) { setError(r.error ?? "The OEM quote could not be read."); return; }
    startEditing(toEdit(r.oemQuote));
  };

  return (
    <div data-testid="oem-quotes-page" style={{ padding: spacing[24] }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: spacing[12], marginBottom: spacing[16] }}>
        <div style={{ flex: 1 }}>
          <h1 style={{
            margin: 0, fontSize: typography.fontSize.xxl, fontWeight: typography.fontWeight.semibold,
            color: semanticColors.textPrimary, letterSpacing: typography.letterSpacing.tight,
          }}>OEM Quotes</h1>
          <div style={{ fontSize: typography.fontSize.base, color: semanticColors.textSecondary }}>
            Quotes our suppliers gave us. A draft line can take its net cost from one; it never
            appears on a customer document.
          </div>
        </div>
        {canEdit && !editing && (
          <button data-testid="oem-new" style={PRIMARY} onClick={() => startEditing(blank())}>
            New OEM quote
          </button>
        )}
      </div>

      <HowItWorks storageKey="anixify.howItWorks.oemQuotes" testId="oem-how">
        <div>OEM = the manufacturer or supplier we buy from. Record here the price quotes they send us.</div>
        <div>On a customer quote, a line from the same OEM with the exact part number can use that price
          ("Use OEM quote price", the sheet icon on the line). The OEM's name, number and price never appear on
          the customer's document.</div>
        <div>Before ordering, the OEM still confirms the final price and lead time on the quote (step 2 there).</div>
      </HowItWorks>

      {editing && (
        <OemQuoteEditor
          key={editorKey}
          initial={editing}
          canEdit={canEdit}
          onClose={() => setEditing(null)}
          onSaved={() => { void load(query); }}
        />
      )}

      <input
        data-testid="oem-search"
        placeholder="Search OEM, quote number, project or part number"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        style={{ ...INPUT, maxWidth: 420, marginBottom: spacing[8] }}
      />
      {error && <div data-testid="oem-error" style={ERROR}>{error}</div>}
      {rows && (
        <>
          <div data-testid="oem-count" style={{ ...NOTE, marginBottom: spacing[8], fontVariantNumeric: "tabular-nums" }}>
            {rows.length} OEM quote{rows.length === 1 ? "" : "s"}
          </div>
          {rows.length === 0 ? (
            <div data-testid="oem-empty" style={NOTE}>
              {query.trim() ? "No OEM quote matches this search. Search by OEM, quote number, project or part number."
                : "No OEM quotes yet. When an OEM sends you a price quote, add it with New OEM quote so a customer quote can use its price."}
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table data-testid="oem-table" style={{ borderCollapse: "collapse", width: "100%" }}>
                <thead>
                  <tr>
                    {["OEM", "Quote no.", "Project", "Quote date", "Valid until", "Currency", "Lines", "Used",
                      "Last edited by"].map((h) => <th key={h} style={TH}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((q) => (
                    <tr key={q.id} data-testid={`oem-row-${q.id}`} onClick={() => void open(q.id)}
                      style={{ cursor: "pointer" }}>
                      <td style={TD}>{q.oem}</td>
                      <td style={TD}>{q.number}</td>
                      <td style={TD}>{q.project_name ?? "—"}</td>
                      <td style={TD}>{q.quote_date ? formatDate(q.quote_date) : "—"}</td>
                      <td style={TD}>
                        {q.valid_until ? formatDate(q.valid_until) : "—"}
                        {q.expired && <span data-testid={`oem-expired-${q.id}`} style={{ ...PILL, marginLeft: spacing[6] }}>Expired</span>}
                      </td>
                      <td style={TD}>{q.currency}</td>
                      <td style={{ ...TD, fontVariantNumeric: "tabular-nums" }}>{q.line_count}</td>
                      <td style={{ ...TD, fontVariantNumeric: "tabular-nums" }}>{q.used_count}</td>
                      <td style={TD}>{q.last_edited_by ?? q.created_by ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const LINE_COLS: { key: keyof OemQuoteLine; label: string; width: number; type?: string }[] = [
  { key: "part_number", label: "Part number", width: 150 },
  { key: "description", label: "Description", width: 200 },
  { key: "qty", label: "Qty", width: 70, type: "number" },
  { key: "uom", label: "UOM", width: 60 },
  { key: "unit_price", label: "Unit price", width: 100, type: "number" },
  { key: "extended_price", label: "Extended price", width: 110, type: "number" },
  { key: "lead_time", label: "Lead time", width: 100 },
  { key: "est_ship_date", label: "Est. ship date", width: 130, type: "date" },
  { key: "shipping_method", label: "Shipping method", width: 120 },
];

function OemQuoteEditor({ initial, canEdit, onClose, onSaved }: {
  initial: OemQuoteEdit; canEdit: boolean; onClose: () => void; onSaved: (q: OemQuote) => void;
}) {
  const [form, setForm] = useState<OemQuoteEdit>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [preview, setPreview] = useState<{ rows: OemQuotePreviewRow[]; errors: number } | null>(null);
  const ro = !canEdit;

  const set = <K extends keyof OemQuoteEdit>(k: K, v: OemQuoteEdit[K]) => {
    setSaved(false);
    setForm((f) => ({ ...f, [k]: v }));
  };
  const setLine = (i: number, k: keyof OemQuoteLine, v: string) => {
    setSaved(false);
    setForm((f) => ({ ...f, lines: f.lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)) }));
  };

  const save = async () => {
    setBusy(true);
    const r = await saveOemQuote(toPayload(form));
    setBusy(false);
    if (!r.ok || !r.oemQuote) { setError(r.error ?? "Not saved."); return; }
    setError("");
    // The saved record, ids included, so the next save edits it.
    setForm(toEdit(r.oemQuote));
    setSaved(true);
    onSaved(r.oemQuote);
  };

  const upload = async (file: File) => {
    setError("");
    try {
      const r = await previewOemQuoteLines(file.name, await fileToBase64(file));
      if (!r.ok) { setPreview(null); setError(r.error ?? "The file could not be read."); return; }
      setPreview({ rows: r.rows ?? [], errors: r.errors ?? 0 });
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const addPreviewed = () => {
    if (!preview) return;
    const good = preview.rows.filter((r) => r.ok && r.line).map((r) => ({ ...(r.line as OemQuoteLine) }));
    // An editor holding only the untouched starter line takes the upload alone.
    const keep = form.lines.filter((l) => l.id || l.part_number.trim());
    set("lines", [...keep, ...good]);
    setPreview(null);
  };

  return (
    <div data-testid="oem-editor" style={BOX}>
      <div style={{ display: "flex", alignItems: "center", gap: spacing[8] }}>
        <strong style={{ flex: 1, fontSize: typography.fontSize.md, color: semanticColors.textPrimary }}>
          {form.id ? `${form.oem} ${form.number}` : "New OEM quote"}
        </strong>
        <button data-testid="oem-close" style={BUTTON} onClick={onClose}>Close</button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: spacing[8] }}>
        <label style={LABEL}>OEM / brand
          <input data-testid="oem-f-oem" style={INPUT} disabled={ro} value={form.oem} onChange={(e) => set("oem", e.target.value)} />
        </label>
        <label style={LABEL}>OEM quote number
          <input data-testid="oem-f-number" style={INPUT} disabled={ro} value={form.number} onChange={(e) => set("number", e.target.value)} />
        </label>
        <label style={LABEL}>Quote date
          <input data-testid="oem-f-date" type="date" style={INPUT} disabled={ro} value={form.quote_date ?? ""} onChange={(e) => set("quote_date", e.target.value)} />
        </label>
        <label style={LABEL}>Valid until
          <input data-testid="oem-f-valid" type="date" style={INPUT} disabled={ro} value={form.valid_until ?? ""} onChange={(e) => set("valid_until", e.target.value)} />
        </label>
        <label style={LABEL}>Project name
          <input data-testid="oem-f-project" style={INPUT} disabled={ro} value={form.project_name ?? ""} onChange={(e) => set("project_name", e.target.value)} />
        </label>
        <label style={LABEL}>Currency
          <select data-testid="oem-f-currency" style={INPUT} disabled={ro} value={form.currency}
            onChange={(e) => set("currency", e.target.value as OemCurrency)}>
            {OEM_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
      </div>
      <label style={LABEL}>Notes
        <textarea data-testid="oem-f-notes" disabled={ro} value={form.notes ?? ""} onChange={(e) => set("notes", e.target.value)}
          style={{ ...INPUT, height: heights.controlLg * 2, padding: spacing[8] }} />
      </label>

      <div style={{ overflowX: "auto" }}>
        <table data-testid="oem-lines" style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {LINE_COLS.map((c) => <th key={c.key} style={{ ...TH, minWidth: c.width }}>{c.label}</th>)}
              {canEdit && <th style={TH} />}
            </tr>
          </thead>
          <tbody>
            {form.lines.map((l, i) => (
              <tr key={l.id ?? `n${i}`} data-testid={`oem-line-${i}`}>
                {LINE_COLS.map((c) => {
                  // A line a customer quote uses keeps its part number and price.
                  const locked = !!l.used && (c.key === "part_number" || c.key === "unit_price");
                  return (
                    <td key={c.key} style={{ ...TD, padding: spacing[4] }}>
                      <input
                        data-testid={`oem-line-${i}-${c.key}`}
                        type={c.type ?? "text"}
                        disabled={ro || locked}
                        title={locked ? "Used on a customer quote: its part number and price cannot change." : undefined}
                        value={l[c.key] === null || l[c.key] === undefined ? "" : String(l[c.key])}
                        onChange={(e) => setLine(i, c.key, e.target.value)}
                        style={{ ...INPUT, width: c.width }}
                      />
                    </td>
                  );
                })}
                {canEdit && (
                  <td style={{ ...TD, padding: spacing[4] }}>
                    <button data-testid={`oem-line-${i}-remove`} style={BUTTON} disabled={!!l.used}
                      title={l.used ? "Used on a customer quote: it cannot be removed." : "Remove this line"}
                      onClick={() => set("lines", form.lines.filter((_, j) => j !== i))}>Remove</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canEdit && (
        <div style={{ display: "flex", gap: spacing[8], flexWrap: "wrap", alignItems: "center" }}>
          <button data-testid="oem-add-line" style={BUTTON}
            onClick={() => set("lines", [...form.lines, { ...EMPTY_LINE }])}>Add line</button>
          <label style={{ ...BUTTON, display: "inline-flex", alignItems: "center" }}>
            Upload lines (CSV / Excel)
            <input data-testid="oem-upload" type="file" accept=".csv,.xlsx" style={{ display: "none" }}
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) void upload(f); }} />
          </label>
          <span style={NOTE}>Columns: part number, description, qty, UOM, unit price, extended price, lead time,
            ship date, shipping method.</span>
        </div>
      )}

      {preview && (
        <div data-testid="oem-preview" style={{ ...BOX, backgroundColor: semanticColors.surface, marginBottom: 0 }}>
          <div style={NOTE}>
            {preview.rows.length - preview.errors} row(s) can be added
            {preview.errors ? `, ${preview.errors} cannot (shown below)` : ""}. Nothing is saved until you save the
            OEM quote.
          </div>
          {preview.rows.filter((r) => !r.ok).map((r) => (
            <div key={r.index} data-testid={`oem-preview-error-${r.index}`} style={ERROR}>Row {r.index}: {r.error}</div>
          ))}
          <div style={{ display: "flex", gap: spacing[8] }}>
            <button data-testid="oem-preview-add" style={PRIMARY} disabled={preview.rows.length === preview.errors}
              onClick={addPreviewed}>Add {preview.rows.length - preview.errors} line(s)</button>
            <button data-testid="oem-preview-cancel" style={BUTTON} onClick={() => setPreview(null)}>Cancel</button>
          </div>
        </div>
      )}

      {error && <div data-testid="oem-save-error" style={ERROR}>{error}</div>}
      {saved && <div data-testid="oem-saved" style={NOTE}>Saved.</div>}
      {canEdit ? (
        <div style={{ display: "flex", gap: spacing[8] }}>
          <button data-testid="oem-save" style={PRIMARY} disabled={busy} onClick={() => void save()}>
            {busy ? "Saving…" : "Save OEM quote"}
          </button>
        </div>
      ) : (
        <div data-testid="oem-readonly" style={NOTE}>Read only: Sales and Admin edit OEM quotes.</div>
      )}
    </div>
  );
}
